import { NgClass, AsyncPipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { fullAutoTuningEnded, fullAutoTuningRunning, fullAutoTuningStopping } from '@app/models/status-resolve';
import { DeviceService } from '@app/services/device.service';
import { ModalService } from '@app/services/modal.service';
import { TuningControllerService } from '@app/services/tuning-controller.service';
import { DeviceParameterFormComponent } from '@app/shared/device-parameter-form/device-parameter-form.component';
import { FullAutoTuningButtonComponent } from '@app/shared/full-auto-tuning-button/full-auto-tuning-button.component';
import { ParameterFormComponent } from '@app/shared/parameter-form/parameter-form.component';
import { ParameterUnitDisplayPipe } from '@app/shared/parameter-unit-display.pipe';
import { RoundFloatPipe } from '@app/shared/round-float.pipe';
import { DeviceParameter } from '@app/store/device-parameter.model';
import { Device } from '@app/store/device.model';
import { TuningControllerParameter, tuningControllerParameterId } from '@app/store/tuning-controller-parameter.model';
import { TuningController } from '@app/store/tuning-controller.model';
import { getCurrentValue } from '@app/util';

interface GainSchedulingParametersOverview {
  threshold: DeviceParameter;
  parameters: DeviceParameter[][];
}

@Component({
  selector: 'app-position-controller-tuning',
  templateUrl: './position-controller-tuning.component.html',
  imports: [
    NgClass,
    DeviceParameterFormComponent,
    ParameterFormComponent,
    FullAutoTuningButtonComponent,
    AsyncPipe,
    ParameterUnitDisplayPipe,
    RoundFloatPipe,
  ],
})
export class PositionControllerTuningComponent {
  private deviceService = inject(DeviceService);
  private tuningControllerService = inject(TuningControllerService);
  private modalService = inject(ModalService);

  device$: Observable<Device>;
  positionControlStrategyParameter$: Observable<DeviceParameter>;
  positionControlStrategyParameters$: Observable<DeviceParameter[]>;
  tuningParameters$: Observable<TuningControllerParameter[]>;
  cascadedControllerConfigurationParameters$: Observable<TuningControllerParameter[]>;
  restTuningParameters$: Observable<TuningControllerParameter[]>;
  controllerTypeParameter$: Observable<TuningControllerParameter>;
  simplePidParameters$: Observable<DeviceParameter[]>;
  cascadedPidParameters$: Observable<DeviceParameter[]>;
  cascadedPidParametersOverview$: Observable<DeviceParameter[][]>;
  hasEncoderConfigurationUsedForVelocity$: Observable<boolean>;
  gainSchedulingParametersOverview$: Observable<GainSchedulingParametersOverview[]>;
  gainSchedulingEnabled$: Observable<boolean>;

  fullAutoTuningEnded = fullAutoTuningEnded;
  fullAutoTuningRunning = fullAutoTuningRunning;
  fullAutoTuningStopping = fullAutoTuningStopping;

  private _tuningController: TuningController;

  @Input()
  set tuningController(tuningController: TuningController) {
    if (tuningController.id !== this.tuningController?.id) {
      this.device$ = this.deviceService.selectDeviceByDeviceSerialNumber(tuningController.deviceSerialNumber);
      const device = getCurrentValue(this.device$);

      this.positionControlStrategyParameter$ = this.deviceService.selectDeviceParameter(
        device,
        'positionControlStrategy',
      );
      this.positionControlStrategyParameters$ = this.deviceService.selectDeviceParameters(
        device,
        'positionControlStrategy',
      );

      this.tuningParameters$ = this.tuningControllerService.selectTuningControllerParameters(tuningController);
      this.cascadedControllerConfigurationParameters$ = this.tuningParameters$.pipe(
        map((parameters) => [parameters[0]]),
      );
      this.restTuningParameters$ = this.tuningParameters$.pipe(map((parameters) => parameters.slice(1)));

      this.controllerTypeParameter$ = this.tuningControllerService.selectTuningControllerParameterById(
        tuningControllerParameterId({ fieldName: 'controllerType' }, tuningController),
      );

      this.simplePidParameters$ = this.deviceService.selectDeviceParameters(device, 'positionControllerSimplePids');
      this.cascadedPidParameters$ = this.deviceService.selectDeviceParameters(device, 'positionControllerCascadedPids');

      this.gainSchedulingParametersOverview$ = combineLatest([
        this.deviceService.selectDeviceParameters(device, 'gainScheduling0'),
        this.deviceService.selectDeviceParameters(device, 'gainScheduling1'),
      ]).pipe(
        map((gainSchedulingAll) =>
          gainSchedulingAll.map((gs) => ({
            threshold: gs[0],
            parameters: [gs.slice(1, 5), gs.slice(5, 9)],
          })),
        ),
      );

      this.cascadedPidParametersOverview$ = this.cascadedPidParameters$.pipe(
        map((parameters) => [parameters.slice(0, 4), parameters.slice(4, 8)]),
      );

      this.hasEncoderConfigurationUsedForVelocity$ = this.deviceService
        .selectDeviceEncoderConfigurationUsedForVelocity(device)
        .pipe(map((ec) => !!ec));

      this.gainSchedulingEnabled$ = this.deviceService
        .selectDeviceParameters(device, 'gainSchedulingEnabled')
        .pipe(map(([parameter]) => !!parameter.value));
    }

    this._tuningController = tuningController;
  }

  get tuningController() {
    return this._tuningController;
  }

  onTuningParametersControlRangeInputChange(value: any) {
    this.updateAutoTuningParameterValuesAndRequestAutoTuning(value);
  }

  onTuningParametersFormSubmit(formGroup: UntypedFormGroup) {
    this.updateAutoTuningParameterValuesAndRequestAutoTuning(formGroup.value);
  }

  onTuningControllerParametersControlSelectChange(value: any) {
    this.updateAutoTuningParameterValuesAndRequestAutoTuning(value);
  }

  onSetAutoTuningEnabled(autoTuningEnabled: boolean) {
    const id = this.tuningController.id;
    this.tuningControllerService.updateTuningController({ autoTuningEnabled, id });
    if (autoTuningEnabled) {
      this.requestAutoTuning();
    }
  }

  requestAutoTuning() {
    const device = getCurrentValue(this.device$);
    this.deviceService.requestControllerAutoTuning(device, 'position');
  }

  onStartFullAutoTuning() {
    const controllerTypeParameter = getCurrentValue(this.controllerTypeParameter$);
    const device = getCurrentValue(this.device$);
    this.deviceService.requestStartPositionFullAutoTuning(device, controllerTypeParameter.value);
  }

  onStopFullAutoTuning() {
    const device = getCurrentValue(this.device$);
    this.deviceService.requestStopFullAutoTuning(device);
  }

  onOpenFullAutoTuningDescription() {
    this.modalService.openDescriptionModal(
      'Position controller full auto-tuning',
      'The "Analyze and tune automatically" feature uses identified mechanical model and Drive configuration to design a model of the whole cascaded position control loop. This model is used to compute optimal position controller gains that ensure minimum  control loop settling time together with safe stability margins. These gains can be used immediately without any extra adjustment.',
    );
  }

  private updateAutoTuningParameterValuesAndRequestAutoTuning(formValues: any) {
    this.tuningControllerService.updateTuningControllerParameterValues(formValues);
    this.requestAutoTuning();
  }

  onOpenQuickOpenModal(searchTerm: string) {
    const device = getCurrentValue(this.device$);
    this.modalService.openQuickOpenModal(device, searchTerm);
  }

  get isFullAutotuningRunning() {
    const device = getCurrentValue(this.device$);
    return fullAutoTuningRunning(device.fullAutoTuningStatus);
  }
}
